<template>
   <div>
      <SpinnerOverlay v-if="!loaded" />
      <div
         v-if="loaded"
         style="display: flex; flex-direction: column; align-items: center; justify-content: center"
      >
         <b-container class="pb-4">
            <div class="d-flex flex-column stay-visible">
               <p class="card-description">
                  You can use your keyboard's arrow keys to move up and down between
                  {{ entityLabelPluralLower }}, and tab or shift+tab to move right and left between
                  fields.
               </p>
               <p class="card-description" v-if="previousPeriod">
                  Placeholder values are values we found in your
                  {{ previousPeriod.label }} study, provided for your reference. To use a field's
                  reference value again in this year, press shift+enter.
               </p>
            </div>
            <FlagsVerifications
               v-if="expenseType !== 'contractors'"
               :expense-type="expenseType"
               :assigneeId="assigneeId"
            />
            <b-alert
               v-if="showContractorPaymentsWarning"
               show
               variant="warning"
               class="mb-3 py-2 permission-warning-alert"
            >
               You do not have permission to edit these items. Contact your administrator.
            </b-alert>
            <div v-if="hasAnyItems">
               <div
                  class="account-data d-flex flex-column"
                  v-for="(account, accountIndex) in accounts(periodId)"
                  :key="accountIndex"
               >
                  <div
                     class="scroll-x my-2"
                     v-if="
                        accountFields &&
                        accountFields[accountIndex] &&
                        accountFields[accountIndex].length > 0
                     "
                  >
                     <h4 class="account-group-title">
                        {{ account }}
                     </h4>
                     <b-table
                        :id="`table-supplies-vendors-${periodId}-${accountIndex}`"
                        :fields="vendorFieldsHeader"
                        primary-key="_rowKey"
                        :sort-by="'vendor'"
                        :sort-desc="false"
                        :items="accountFields[accountIndex]"
                        :key="`table-supplies-vendors-${periodId}-${accountIndex}`"
                        sticky-header="500px"
                        @sort-changed="
                           (ctx) => onTableSortChanged(ctx, accountFields[accountIndex])
                        "
                     >
                        <template #thead-top>
                           <b-tr class="top-heading-row">
                              <b-th
                                 v-if="basicVendorFieldsHeader.length > 1"
                                 :colspan="basicVendorFieldsHeader.length"
                              >
                                 <div
                                    v-if="anyCompanySelected(accountIndex)"
                                    class="d-flex align-items-center"
                                 >
                                    <b-button
                                       variant="primary"
                                       size="sm"
                                       @click="setCompaniesToZero(accountIndex)"
                                    >
                                       Set selected accounts contributions to 0%
                                    </b-button>
                                 </div>
                              </b-th>
                              <b-th
                                 v-if="vendorFieldsHeader.length > 1"
                                 :colspan="
                                    vendorFieldsHeader.length - basicVendorFieldsHeader.length
                                 "
                              >
                                 <p class="table-info-row">
                                    The sum of these columns must be 100%.
                                 </p>
                              </b-th>
                           </b-tr>
                        </template>
                        <template #head(vendor)>
                           <div style="font-size: 0.75rem" class="d-flex flex-row align-items-end">
                              <div class="d-flex align-items-end">
                                 <b-form-checkbox
                                    v-if="!isCompleted"
                                    style="height: 25px"
                                    class="icon-btn icon-btn-secondary"
                                    @change="
                                       (checked) => unselectAllCompanies(checked, accountIndex)
                                    "
                                    tabindex="-1"
                                    size="lg"
                                    v-model="selectAllCompanies[accountIndex]"
                                    :disabled="isReadOnlyForAssignee()"
                                 >
                                 </b-form-checkbox>
                              </div>
                              Name
                           </div>
                        </template>
                        <template #cell(vendor)="data">
                           <div class="d-flex align-items-center">
                              <b-form-checkbox
                                 v-if="companySelection[accountIndex] && !isCompleted"
                                 tabindex="-1"
                                 size="lg"
                                 :value="true"
                                 :key="`checkbox-vendor-${data.item._rowKey}`"
                                 v-model="companySelection[accountIndex][data.item.id]"
                                 :disabled="isReadOnlyForAssignee()"
                              ></b-form-checkbox>

                              <b style="font-size: 0.75rem">{{ data.value }}</b>
                           </div>
                        </template>

                        <template #cell(totalDebit)="data">
                           <div style="font-size: 0.75rem">{{ formatMoney(data.value) }}</div>
                        </template>
                        <template #cell(totalCredit)="data">
                           <div style="font-size: 0.75rem">{{ formatMoney(data.value) }}</div>
                        </template>
                        <template #cell(netAmount)="data">
                           <div style="font-size: 0.75rem">{{ formatMoney(data.value.value) }}</div>
                        </template>
                        <template #cell(location)="data">
                           <div style="font-size: 0.75rem">{{ data.value }}</div>
                        </template>

                        <template #cell(status)="data" style="min-width: 200px">
                           <RdigStatusLabel
                              :ident="`status-${periodId}-manual`"
                              class="mr-2"
                              :status="vendorStatus(data.item)"
                           />
                        </template>
                        <template class="table-gray-light text-dark" #cell(input)="data">
                           <div>
                              <VendorPercentageInput
                                 :grid-section="'accounts'"
                                 :grid-table-index="accountIndex"
                                 :grid-row-index="data.index"
                                 :grid-col-index="0"
                                 :ident="`${periodId}-${account}-${data.index}`"
                                 :assigneeId="assigneeId"
                                 :summary="data.item"
                                 v-grid-keynav="{
                                    section: 'accounts',
                                    tableIndex: accountIndex,
                                    rowIndex: data.index,
                                    colIndex: 0, // first editable column
                                    type: 'percentage',
                                 }"
                                 :placeholder="
                                    getImportedVendorPlaceholderValue({
                                       vendor: data.item.vendor,
                                       location: data.item.location,
                                       account,
                                       path: ['percentage', 'value'],
                                    })
                                 "
                                 :disabled="isAccountPercentageInputDisabled(data.item)"
                                 :expense-type="expenseType"
                                 class="time-percentage-input"
                              />
                           </div>
                        </template>
                        <template
                           v-for="(project, colIdx) in projects"
                           #[projectColKey(project.id)]="data"
                        >
                           <transition
                              :key="`${data.item._rowKey}-${project.id}`"
                              :name="dimensionTransitionName"
                           >
                              <ProjectPercentageInput
                                 v-if="!hasActivitiesDimension || !showingActivities"
                                 grid-section="accounts"
                                 :grid-table-index="accountIndex"
                                 :grid-row-index="data.index"
                                 :grid-col-index="colIdx + 1"
                                 :has-warning="rowHasProjectSumWarning(data.item)"
                                 :assigneeId="assigneeId"
                                 :project-id="String(project.id)"
                                 :ident="`${periodId}-adhoc-${data.index}`"
                                 :summary="data.item"
                                 :value="
                                    data.item.projectsData
                                       ? data.item.projectsData[String(project.id)]
                                       : null
                                 "
                                 v-grid-keynav="{
                                    section: 'accounts',
                                    tableIndex: accountIndex,
                                    rowIndex: data.index,
                                    colIndex: colIdx + 1,
                                    type: 'project',
                                 }"
                                 :placeholder="
                                    getImportedVendorPlaceholderValue({
                                       vendor: data.item.vendor,
                                       location: data.item.location,
                                       account,
                                       path: projectDataPath(project.id),
                                    })
                                 "
                                 :disabled="
                                    isAccountProjectInputDisabled(data.item, String(project.id))
                                 "
                                 :project-ids="projects.map(({id}) => id)"
                                 :expense-type="expenseType"
                                 class="time-percentage-input"
                              />
                           </transition>
                        </template>
                        <template
                           v-for="(activity, colIdx) in activities"
                           #[activityColKey(activity.id)]="data"
                        >
                           <transition
                              :key="`${data.item._rowKey}-${activity.id}`"
                              :name="dimensionTransitionName"
                           >
                              <ActivityPercentageInput
                                 v-if="showingActivities"
                                 grid-section="accounts"
                                 :grid-table-index="accountIndex"
                                 :grid-row-index="data.index"
                                 :grid-col-index="colIdx + 1"
                                 :has-warning="rowHasActivitySumWarning(data.item)"
                                 :assigneeId="assigneeId"
                                 :activity-id="String(activity.id)"
                                 :ident="`${periodId}-adhoc-${data.index}`"
                                 :summary="data.item"
                                 :value="
                                    data.item.activitiesData
                                       ? data.item.activitiesData[String(activity.id)]
                                       : null
                                 "
                                 v-grid-keynav="{
                                    section: 'accounts',
                                    tableIndex: accountIndex,
                                    rowIndex: data.index,
                                    colIndex: colIdx + 1,
                                    type: 'activity',
                                 }"
                                 :placeholder="
                                    getImportedVendorPlaceholderValue({
                                       vendor: data.item.vendor,
                                       location: data.item.location,
                                       account,
                                       path: activityDataPath(activity.id),
                                    })
                                 "
                                 :disabled="
                                    isAccountActivityInputDisabled(data.item, String(activity.id))
                                 "
                                 :activity-ids="activities.map(({id}) => id)"
                                 :expense-type="expenseType"
                                 class="time-percentage-input"
                              />
                           </transition>
                        </template>
                     </b-table>
                  </div>
               </div>
            </div>
            <div
               class="account-data d-flex flex-column"
               v-for="(account, groupIndex) in vendorAccountNames"
               :key="`vendor-account-${groupIndex}`"
            >
               <div
                  class="scroll-x my-2"
                  v-if="
                     vendorAccountFields[groupIndex] && vendorAccountFields[groupIndex].length > 0
                  "
               >
                  <h4 class="account-group-title">{{ account }}</h4>
                  <b-table
                     :id="`table-manual-vendors-${periodId}-${groupIndex}`"
                     :key="`table-manual-vendors-${periodId}-${groupIndex}`"
                     :fields="adHocVendorFields(vendorAccountFields[groupIndex])"
                     :items="vendorAccountFields[groupIndex]"
                     :sort-by="'vendor'"
                     :sort-desc="false"
                     primary-key="_rowKey"
                     :tbody-tr-class="manualVendorRowClass"
                     sticky-header="500px"
                     @sort-changed="
                        (ctx) => onTableSortChanged(ctx, vendorAccountFields[groupIndex])
                     "
                  >
                     <template #thead-top>
                        <b-tr class="top-heading-row">
                           <b-th
                              v-if="
                                 adHocVendorFields(vendorAccountFields[groupIndex]).length -
                                    dimensionFields.length >
                                 0
                              "
                              :colspan="
                                 adHocVendorFields(vendorAccountFields[groupIndex]).length -
                                 dimensionFields.length
                              "
                           >
                              <div
                                 v-if="anyVendorSelected(vendorAccountFields[groupIndex])"
                                 class="d-flex align-items-center"
                              >
                                 <b-button
                                    variant="primary"
                                    size="sm"
                                    @click="setVendorsToZero(vendorAccountFields[groupIndex])"
                                 >
                                    Set selected {{ entityLabelPluralLower }} contributions to 0%
                                 </b-button>
                              </div></b-th
                           >
                           <b-th v-if="dimensionFields.length > 0" :colspan="dimensionFields.length"
                              ><p class="table-info-row">
                                 The sum of these columns must be 100%.
                              </p></b-th
                           >
                        </b-tr>
                     </template>

                     <template #head(vendor)>
                        <div style="font-size: 0.75rem" class="d-flex flex-row align-items-end">
                           <div class="d-flex align-items-end">
                              <b-form-checkbox
                                 v-if="!isCompleted"
                                 style="height: 27px"
                                 class="icon-btn icon-btn-secondary"
                                 @change="
                                    (checked) =>
                                       unselectAllVendors(checked, vendorAccountFields[groupIndex])
                                 "
                                 size="lg"
                                 tabindex="-1"
                                 :checked="
                                    isVendorGroupFullySelected(vendorAccountFields[groupIndex])
                                 "
                                 :disabled="isReadOnlyForAssignee()"
                              >
                              </b-form-checkbox>
                           </div>
                           Name
                        </div>
                     </template>
                     <template #cell(vendor)="data">
                        <div class="d-flex align-items-center">
                           <b-form-checkbox
                              v-if="vendorSelection && !isCompleted"
                              tabindex="-1"
                              size="lg"
                              :value="true"
                              v-model="vendorSelection[getManualVendorId(data.item)]"
                              :disabled="isManualVendorInputDisabled(data.item)"
                           ></b-form-checkbox>

                           <b style="font-size: 0.75rem">{{ data.value }}</b>
                           <span
                              v-if="isContractorRowLocked(data.item)"
                              class="contractor-lock-note"
                           >
                              Locked
                           </span>
                        </div>
                     </template>
                     <template #cell(totalDebit)="data">
                        <VendorAmountInput
                           grid-section="vendors"
                           :grid-table-index="groupIndex"
                           :grid-row-index="data.index"
                           :grid-col-index="0"
                           :ident="`${periodId}-${data.index}`"
                           :summary="data.item"
                           :disabled="isManualVendorInputDisabled(data.item)"
                           :assigneeId="assigneeId"
                           :placeholder="
                              getManualVendorPlaceholderValue({
                                 manualVendorId: getManualVendorId(data.item),
                                 location: data.item.location,
                                 path: ['totalDebit'],
                                 formatter: (value) => (value / 100).toFixed(2),
                              })
                           "
                           v-grid-keynav="{
                              section: 'vendors',
                              tableIndex: groupIndex,
                              rowIndex: data.index,
                              colIndex: 0, // first editable column in vendors table
                              type: 'amount',
                           }"
                           :expense-type="expenseType"
                        />
                     </template>
                     <template #cell(netAmount)="data">
                        <div v-if="isImportedEmployeeRow(data.item)" style="font-size: 0.75rem">
                           {{ formatMoney(data.value.value) }}
                        </div>
                        <VendorAmountInput
                           v-else
                           grid-section="vendors"
                           :grid-table-index="groupIndex"
                           :grid-row-index="data.index"
                           :grid-col-index="0"
                           :ident="`${periodId}-${data.index}`"
                           :summary="data.item"
                           :disabled="isManualVendorInputDisabled(data.item)"
                           :assigneeId="assigneeId"
                           :placeholder="
                              getManualVendorPlaceholderValue({
                                 manualVendorId: getManualVendorId(data.item),
                                 location: data.item.location,
                                 path: ['totalDebit'],
                                 formatter: (value) => (value / 100).toFixed(2),
                              })
                           "
                           v-grid-keynav="{
                              section: 'vendors',
                              tableIndex: groupIndex,
                              rowIndex: data.index,
                              colIndex: 0, // first editable column in vendors table
                              type: 'amount',
                           }"
                           :expense-type="expenseType"
                        />
                     </template>
                     <template #cell(status)="data" style="min-width: 200px">
                        <RdigStatusLabel
                           :ident="`status-${periodId}-manual-${getManualVendorId(data.item)}`"
                           class="mr-2"
                           :status="vendorStatus(data.item)"
                        />
                     </template>

                     <template class="table-gray-light text-dark" #cell(input)="data">
                        <VendorPercentageInput
                           grid-section="vendors"
                           :grid-table-index="groupIndex"
                           :grid-row-index="data.index"
                           :grid-col-index="1"
                           :ident="`${periodId}-adhoc-${getManualVendorId(data.item)}`"
                           :summary="data.item"
                           :assigneeId="assigneeId"
                           :placeholder="
                              getManualVendorPlaceholderValue({
                                 manualVendorId: getManualVendorId(data.item),
                                 location: data.item.location,
                                 path: ['percentage', 'value'],
                              })
                           "
                           :disabled="isManualVendorInputDisabled(data.item)"
                           :project-ids="projects.map(({id}) => id)"
                           v-grid-keynav="{
                              section: 'vendors',
                              tableIndex: groupIndex,
                              rowIndex: data.index,
                              colIndex: 1, // second editable column
                              type: 'percentage',
                           }"
                           :expense-type="expenseType"
                           class="time-percentage-input"
                        />
                     </template>
                     <template
                        v-for="(project, colIdx) in projects"
                        #[projectColKey(project.id)]="data"
                     >
                        <transition
                           :key="`${periodId}-${project.id}-${data.index}-${getManualVendorId(
                              data.item
                           )}`"
                           :name="dimensionTransitionName"
                        >
                           <ProjectPercentageInput
                              v-if="!hasActivitiesDimension || !showingActivities"
                              grid-section="vendors"
                              :grid-table-index="groupIndex"
                              :grid-row-index="data.index"
                              :grid-col-index="colIdx + 2"
                              :has-warning="rowHasProjectSumWarning(data.item)"
                              :project-id="String(project.id)"
                              :assigneeId="assigneeId"
                              :ident="`${periodId}-adhoc-${data.index}-${getManualVendorId(
                                 data.item
                              )}`"
                              :summary="data.item"
                              :value="
                                 data.item.projectsData
                                    ? data.item.projectsData[String(project.id)]
                                    : null
                              "
                              v-grid-keynav="{
                                 section: 'vendors',
                                 tableIndex: groupIndex,
                                 rowIndex: data.index,
                                 colIndex: colIdx + 2, // after amount + vendor %
                                 type: 'project',
                              }"
                              :placeholder="
                                 getManualVendorPlaceholderValue({
                                    manualVendorId: getManualVendorId(data.item),
                                    location: data.item.location,
                                    path: projectDataPath(project.id),
                                 })
                              "
                              :disabled="
                                 isManualVendorProjectInputDisabled(data.item, String(project.id))
                              "
                              :project-ids="projects.map(({id}) => id)"
                              :expense-type="expenseType"
                              class="time-percentage-input"
                           />
                        </transition>
                     </template>
                     <template
                        v-for="(activity, colIdx) in activities"
                        #[activityColKey(activity.id)]="data"
                     >
                        <transition
                           :key="`${periodId}-${activity.id}-${data.index}-${getManualVendorId(
                              data.item
                           )}`"
                           :name="dimensionTransitionName"
                        >
                           <ActivityPercentageInput
                              v-if="showingActivities"
                              grid-section="vendors"
                              :grid-table-index="groupIndex"
                              :grid-row-index="data.index"
                              :grid-col-index="colIdx + 2"
                              :has-warning="rowHasActivitySumWarning(data.item)"
                              :activity-id="String(activity.id)"
                              :assigneeId="assigneeId"
                              :ident="`${periodId}-adhoc-${data.index}-${getManualVendorId(
                                 data.item
                              )}`"
                              :summary="data.item"
                              :value="
                                 data.item.activitiesData
                                    ? data.item.activitiesData[String(activity.id)]
                                    : null
                              "
                              v-grid-keynav="{
                                 section: 'vendors',
                                 tableIndex: groupIndex,
                                 rowIndex: data.index,
                                 colIndex: colIdx + 2, // after amount + vendor %
                                 type: 'activity',
                              }"
                              :placeholder="
                                 getManualVendorPlaceholderValue({
                                    manualVendorId: getManualVendorId(data.item),
                                    location: data.item.location,
                                    path: activityDataPath(activity.id),
                                 })
                              "
                              :disabled="
                                 isManualVendorActivityInputDisabled(data.item, String(activity.id))
                              "
                              :activity-ids="activities.map(({id}) => id)"
                              :expense-type="expenseType"
                              class="time-percentage-input"
                           />
                        </transition>
                     </template>
                  </b-table>
               </div>
            </div>

            <div class="account-data">
               <div
                  class="scroll-x my-2"
                  v-if="manualVendorFields && manualVendorFields.length > 0"
               >
                  <h4 class="account-group-title">
                     {{ manuallyAddedSectionLabel }}
                  </h4>
                  <b-table
                     :id="`table-supplies-vendors-${periodId}-manual`"
                     :key="`table-supplies-vendors-${periodId}-manual`"
                     :fields="adHocVendorFields(manualVendorFields)"
                     :items="manualVendorFields"
                     :sort-by="'vendor'"
                     :sort-desc="false"
                     primary-key="_rowKey"
                     :tbody-tr-class="manualVendorRowClass"
                     sticky-header="500px"
                     @sort-changed="(ctx) => onTableSortChanged(ctx, manualVendorFields)"
                  >
                     <template #thead-top>
                        <b-tr class="top-heading-row">
                           <b-th
                              v-if="
                                 adHocVendorFields(manualVendorFields).length -
                                    dimensionFields.length >
                                 0
                              "
                              :colspan="
                                 adHocVendorFields(manualVendorFields).length -
                                 dimensionFields.length
                              "
                           >
                              <div
                                 v-if="anyVendorSelected(manualVendorFields)"
                                 class="d-flex align-items-center"
                              >
                                 <b-button
                                    variant="primary"
                                    size="sm"
                                    @click="setVendorsToZero(manualVendorFields)"
                                 >
                                    Set selected {{ entityLabelPluralLower }} contributions to 0%
                                 </b-button>
                              </div></b-th
                           >
                           <b-th v-if="dimensionFields.length > 0" :colspan="dimensionFields.length"
                              ><p class="table-info-row">
                                 The sum of these columns must be 100%.
                              </p></b-th
                           >
                        </b-tr>
                     </template>

                     <template #head(vendor)>
                        <div style="font-size: 0.75rem" class="d-flex flex-row align-items-end">
                           <div class="d-flex align-items-end">
                              <b-form-checkbox
                                 v-if="!isCompleted"
                                 style="height: 27px"
                                 class="icon-btn icon-btn-secondary"
                                 @change="
                                    (checked) => unselectAllVendors(checked, manualVendorFields)
                                 "
                                 size="lg"
                                 tabindex="-1"
                                 :checked="isVendorGroupFullySelected(manualVendorFields)"
                                 :disabled="isReadOnlyForAssignee()"
                              >
                              </b-form-checkbox>
                           </div>
                           Name
                        </div>
                     </template>
                     <template #cell(vendor)="data">
                        <div class="d-flex align-items-center">
                           <b-form-checkbox
                              v-if="vendorSelection && !isCompleted"
                              tabindex="-1"
                              size="lg"
                              :value="true"
                              v-model="vendorSelection[getManualVendorId(data.item)]"
                              :disabled="isManualVendorInputDisabled(data.item)"
                           ></b-form-checkbox>

                           <b style="font-size: 0.75rem">{{ data.value }}</b>
                           <span
                              v-if="isContractorRowLocked(data.item)"
                              class="contractor-lock-note"
                           >
                              Locked
                           </span>
                        </div>
                     </template>
                     <template #cell(totalDebit)="data">
                        <VendorAmountInput
                           grid-section="vendors"
                           :grid-table-index="vendorAccountNames.length"
                           :grid-row-index="data.index"
                           :grid-col-index="0"
                           :ident="`${periodId}-${data.index}`"
                           :summary="data.item"
                           :disabled="isManualVendorInputDisabled(data.item)"
                           :assigneeId="assigneeId"
                           :placeholder="
                              getManualVendorPlaceholderValue({
                                 manualVendorId: getManualVendorId(data.item),
                                 location: data.item.location,
                                 path: ['totalDebit'],
                                 formatter: (value) => (value / 100).toFixed(2),
                              })
                           "
                           v-grid-keynav="{
                              section: 'vendors',
                              tableIndex: vendorAccountNames.length,
                              rowIndex: data.index,
                              colIndex: 0, // first editable column in vendors table
                              type: 'amount',
                           }"
                           :expense-type="expenseType"
                        />
                     </template>
                     <template #cell(netAmount)="data">
                        <div v-if="isImportedEmployeeRow(data.item)" style="font-size: 0.75rem">
                           {{ formatMoney(data.value.value) }}
                        </div>
                        <VendorAmountInput
                           v-else
                           grid-section="vendors"
                           :grid-table-index="vendorAccountNames.length"
                           :grid-row-index="data.index"
                           :grid-col-index="0"
                           :ident="`${periodId}-${data.index}`"
                           :summary="data.item"
                           :disabled="isEmployeeWagesInputDisabled(data.item)"
                           :assigneeId="assigneeId"
                           :placeholder="
                              getManualVendorPlaceholderValue({
                                 manualVendorId: getManualVendorId(data.item),
                                 location: data.item.location,
                                 path: ['totalDebit'],
                                 formatter: (value) => (value / 100).toFixed(2),
                              })
                           "
                           v-grid-keynav="{
                              section: 'vendors',
                              tableIndex: vendorAccountNames.length,
                              rowIndex: data.index,
                              colIndex: 0, // first editable column in vendors table
                              type: 'amount',
                           }"
                           :expense-type="expenseType"
                        />
                     </template>
                     <template #cell(status)="data" style="min-width: 200px">
                        <RdigStatusLabel
                           :ident="`status-${periodId}-manual-${getManualVendorId(data.item)}`"
                           class="mr-2"
                           :status="vendorStatus(data.item)"
                        />
                     </template>

                     <template class="table-gray-light text-dark" #cell(input)="data">
                        <VendorPercentageInput
                           grid-section="vendors"
                           :grid-table-index="vendorAccountNames.length"
                           :grid-row-index="data.index"
                           :grid-col-index="1"
                           :ident="`${periodId}-adhoc-${getManualVendorId(data.item)}`"
                           :summary="data.item"
                           :assigneeId="assigneeId"
                           :placeholder="
                              getManualVendorPlaceholderValue({
                                 manualVendorId: getManualVendorId(data.item),
                                 location: data.item.location,
                                 path: ['percentage', 'value'],
                              })
                           "
                           :disabled="isManualVendorInputDisabled(data.item)"
                           :project-ids="projects.map(({id}) => id)"
                           v-grid-keynav="{
                              section: 'vendors',
                              tableIndex: vendorAccountNames.length,
                              rowIndex: data.index,
                              colIndex: 1, // second editable column
                              type: 'percentage',
                           }"
                           :expense-type="expenseType"
                           class="time-percentage-input"
                        />
                     </template>
                     <template
                        v-for="(project, colIdx) in projects"
                        #[projectColKey(project.id)]="data"
                     >
                        <transition
                           :key="`${periodId}-${project.id}-${data.index}-${getManualVendorId(
                              data.item
                           )}`"
                           :name="dimensionTransitionName"
                        >
                           <ProjectPercentageInput
                              v-if="!hasActivitiesDimension || !showingActivities"
                              grid-section="vendors"
                              :grid-table-index="vendorAccountNames.length"
                              :grid-row-index="data.index"
                              :grid-col-index="colIdx + 2"
                              :has-warning="rowHasProjectSumWarning(data.item)"
                              :project-id="String(project.id)"
                              :assigneeId="assigneeId"
                              :ident="`${periodId}-adhoc-${data.index}-${getManualVendorId(
                                 data.item
                              )}`"
                              :summary="data.item"
                              :value="
                                 data.item.projectsData
                                    ? data.item.projectsData[String(project.id)]
                                    : null
                              "
                              v-grid-keynav="{
                                 section: 'vendors',
                                 tableIndex: vendorAccountNames.length,
                                 rowIndex: data.index,
                                 colIndex: colIdx + 2, // after amount + vendor %
                                 type: 'project',
                              }"
                              :placeholder="
                                 getManualVendorPlaceholderValue({
                                    manualVendorId: getManualVendorId(data.item),
                                    location: data.item.location,
                                    path: projectDataPath(project.id),
                                 })
                              "
                              :disabled="
                                 isManualVendorProjectInputDisabled(data.item, String(project.id))
                              "
                              :project-ids="projects.map(({id}) => id)"
                              :expense-type="expenseType"
                              class="time-percentage-input"
                           />
                        </transition>
                     </template>
                     <template
                        v-for="(activity, colIdx) in activities"
                        #[activityColKey(activity.id)]="data"
                     >
                        <transition
                           :key="`${periodId}-${activity.id}-${data.index}-${getManualVendorId(
                              data.item
                           )}`"
                           :name="dimensionTransitionName"
                        >
                           <ActivityPercentageInput
                              v-if="showingActivities"
                              grid-section="vendors"
                              :grid-table-index="vendorAccountNames.length"
                              :grid-row-index="data.index"
                              :grid-col-index="colIdx + 2"
                              :has-warning="rowHasActivitySumWarning(data.item)"
                              :activity-id="String(activity.id)"
                              :assigneeId="assigneeId"
                              :ident="`${periodId}-adhoc-${data.index}-${getManualVendorId(
                                 data.item
                              )}`"
                              :summary="data.item"
                              :value="
                                 data.item.activitiesData
                                    ? data.item.activitiesData[String(activity.id)]
                                    : null
                              "
                              v-grid-keynav="{
                                 section: 'vendors',
                                 tableIndex: vendorAccountNames.length,
                                 rowIndex: data.index,
                                 colIndex: colIdx + 2, // after amount + vendor %
                                 type: 'activity',
                              }"
                              :placeholder="
                                 getManualVendorPlaceholderValue({
                                    manualVendorId: getManualVendorId(data.item),
                                    location: data.item.location,
                                    path: activityDataPath(activity.id),
                                 })
                              "
                              :disabled="
                                 isManualVendorActivityInputDisabled(data.item, String(activity.id))
                              "
                              :activity-ids="activities.map(({id}) => id)"
                              :expense-type="expenseType"
                              class="time-percentage-input"
                           />
                        </transition>
                     </template>
                  </b-table>
               </div>
            </div>
            <div v-if="hasMultipleActivities" class="dimension-tab-switch mb-2">
               <b-button
                  id="btn-step-back"
                  class="step-button action-button"
                  variant="primary"
                  size="sm"
                  v-if="step === 1"
                  @click="onStepClick"
               >
                  <b-icon-arrow-left /> Previous (Projects)
               </b-button>
               <b-button
                  id="btn-next-step"
                  class="step-button dark-button action-button"
                  variant="primary"
                  size="sm"
                  v-if="step === 0"
                  @click="onStepClick"
               >
                  Next (Activities) <b-icon-arrow-right />
               </b-button>
            </div>
         </b-container>
      </div>
   </div>
</template>

<script>
import Vue from 'vue';
import {mapGetters} from 'vuex';
import {FieldStatus} from '@/store/utils';
import {focusCellMixin, expensePermissionsMixin} from '@/mixins';
import ProjectPercentageInput from './ProjectPercentageInput.vue';
import ActivityPercentageInput from './ActivityPercentageInput.vue';
import VendorPercentageInput from './VendorPercentageInput.vue';
import VendorAmountInput from './VendorAmountInput.vue';
import RdigStatusLabel from '@/components/RdigStatusLabel.vue';
import {
   sortBy,
   hasProjectSumWarning as hasProjectSumWarningForRow,
   hasActivitySumWarning as hasActivitySumWarningForRow,
   isNil,
   hasContractorPaymentsPermission as checkContractorPaymentsPermission,
} from '@/helpers/utils';
import SpinnerOverlay from '@/views/common/widgets/SpinnerOverlay';
import {dataStatus} from '@/helpers/utils';
import FlagsVerifications from './FlagsVerifications.vue';
import {EXPENSE_ENTITY_LABEL_CONFIG} from '@/helpers/baseConfig';

export default {
   components: {
      FlagsVerifications,
      VendorAmountInput,
      VendorPercentageInput,
      ProjectPercentageInput,
      ActivityPercentageInput,
      RdigStatusLabel,
      SpinnerOverlay,
   },
   props: {
      assigneeId: {
         type: String,
      },
      expenseType: {
         type: String,
         required: true,
         validator: (value) => ['cloud', 'supplies', 'contractors', 'employees'].includes(value),
      },
   },
   mixins: [focusCellMixin, expensePermissionsMixin],
   data() {
      return {
         FieldStatus,
         companySelection: {},
         vendorSelection: {},
         statusSortSnapshots: {},
         loaded: false,
         normalizingNegativeAccountRows: false,
         StatusType: this.$constants().StatusType,
         setAllInput: {},
         selectAllCompanies: [],
         step: 0,
      };
   },

   computed: {
      ...mapGetters({
         profile: 'profile',
         activeStudyId: 'companies/activeStudyId',
         studyPeriodMap: 'companies/studyPeriodMap',
         projectsInPeriod: 'projects/projectsInPeriod',
         isSME: 'isSME',
         usStates: 'usStates',
      }),

      accountData() {
         return this.$store.getters[`${this.expenseType}/accountData`];
      },

      adHocData() {
         return this.$store.getters[`${this.expenseType}/adHocData`];
      },

      vendorsMap() {
         return this.$store.getters[`${this.expenseType}/vendorsMap`];
      },

      importedVendorsMap() {
         return this.$store.getters[`${this.expenseType}/importedVendorsMap`];
      },

      isCompleted() {
         return this.$store.getters[`${this.expenseType}/isCompleted`];
      },

      declaration() {
         return this.$store.getters[`${this.expenseType}/declaration`];
      },

      periodId() {
         return this.$route.params.periodId;
      },

      projects() {
         return this.projectsInPeriod(this.periodId).sort(sortBy('name'));
      },

      hasActivitiesDimension() {
         return this.expenseType === 'employees';
      },

      activities() {
         return this.hasActivitiesDimension ? this.$store.getters['activities/activities'] : [];
      },

      // With a single activity there's nowhere else the split could go, so
      // the switch to a second tab is pointless -- keep the table on Projects.
      hasMultipleActivities() {
         return this.hasActivitiesDimension && this.activities.length > 1;
      },

      showingActivities() {
         // Gated on hasMultipleActivities (not just hasActivitiesDimension) so
         // that if activities collapse to 1 while step is still 1 -- the tab
         // switcher's now the only way to change step, and it's hidden in that
         // case -- the view falls back to Projects instead of getting stuck.
         return this.hasMultipleActivities && this.step === 1;
      },

      dimensionTransitionName() {
         return this.step === 1 ? 'dimension-slide-forward' : 'dimension-slide-back';
      },

      periodIds() {
         return this.$store.getters[`${this.expenseType}/supportedPeriodIds`];
      },

      periodMap() {
         return this.periodIds.map((periodId) => this.studyPeriodMap[periodId]);
      },

      previousPeriodIds() {
         const currentPeriodIndex = this.periodMap.findIndex(
            ({id}) => Number(id) === Number(this.periodId)
         );
         return this.periodMap.slice(0, currentPeriodIndex).map(({id}) => id);
      },

      previousPeriod() {
         const periodIndex = this.periodMap.findIndex(
            (period) => period.id === Number(this.periodId)
         );
         return periodIndex > 0 ? this.periodMap[periodIndex - 1] : null;
      },

      entityLabelPlural() {
         return (
            EXPENSE_ENTITY_LABEL_CONFIG[this.expenseType]?.plural ||
            EXPENSE_ENTITY_LABEL_CONFIG.supplies.plural
         );
      },

      entityLabelPluralLower() {
         return (
            EXPENSE_ENTITY_LABEL_CONFIG[this.expenseType]?.pluralLower ||
            EXPENSE_ENTITY_LABEL_CONFIG.supplies.pluralLower
         );
      },

      manuallyAddedSectionLabel() {
         return `Manually Added ${this.entityLabelPlural}`;
      },

      projectFields() {
         return (this.projects || []).map((project) => ({
            key: `project_${project.id}`,
            label: project.name,
            sortable: false,
            variant: 'gray-light',
            thStyle: {fontSize: '.75rem'},
         }));
      },

      activityFields() {
         return (this.activities || []).map((activity) => ({
            key: `activity_${activity.id}`,
            label: activity.name,
            sortable: false,
            variant: 'gray-light',
            thStyle: {fontSize: '.75rem'},
         }));
      },

      dimensionFields() {
         return this.showingActivities ? this.activityFields : this.projectFields;
      },

      manyUSLocations() {
         return {
            key: 'location',
            sortable: true,
            formatter: (val) => this.usStates[val],
            sortByFormatted: true,
            thStyle: {fontSize: '.75rem'},
         };
      },

      defaultVendorFields() {
         const fields = [];
         if (this.showSensitiveAmountColumn) {
            const amountField = this.expenseType === 'contractors' ? 'totalDebit' : 'netAmount';
            const amountLabel =
               this.expenseType === 'contractors'
                  ? 'Payment Info'
                  : this.expenseType === 'employees'
                  ? 'Employee Taxable Wages'
                  : 'Net Amount';
            fields.push({
               key: amountField,
               label: amountLabel,
               sortable: true,
               thStyle: {fontSize: '.75rem'},
            });
         }

         fields.push(
            {
               key: 'status',
               label: 'Status',
               sortable: true,
               formatter: (_val, _key, item) => this.statusSortValue(item),
               sortByFormatted: true,
               thStyle: {fontSize: '.75rem'},
            },
            {
               key: 'input',
               label: 'Percent of Net spent on R&D',
               variant: 'gray-dark',
               thStyle: {fontSize: '.75rem', color: '#999999'},
            }
         );

         return fields;
      },

      showSensitiveAmountColumn() {
         if (this.expenseType === 'contractors') {
            return this.viewerHasContractorPaymentsPermission;
         }

         if (this.expenseType === 'employees') {
            return this.hasPayrollAccess;
         }

         return true;
      },

      defaultTotalVendofFields() {
         return [
            {
               key: 'totalDebit',
               label: 'Total Debits',
               sortable: true,
               thStyle: {fontSize: '.75rem'},
            },
            {
               key: 'totalCredit',
               label: 'Total Credits',
               sortable: true,
               thStyle: {fontSize: '.75rem'},
            },
         ];
      },

      hasLocation() {
         return this.declaration === 'MANY' || this.expenseType === 'contractors';
      },

      basicAdHocVendorFields() {
         const fields = [
            {
               key: 'vendor',
               label: 'Name',
               sortable: true,
               thStyle: {},
            },
         ];

         this.hasLocation && fields.push(this.manyUSLocations);

         fields.push(...this.defaultVendorFields);

         return fields;
      },

      basicVendorFieldsHeader() {
         const fields = [
            {
               key: 'vendor',
               label: 'Name',
               sortable: true,
               thStyle: {fontSize: '.75rem'},
            },
         ];

         this.hasLocation && fields.push(this.manyUSLocations);

         fields.push(...[...this.defaultTotalVendofFields, ...this.defaultVendorFields]);

         return fields;
      },

      vendorFieldsHeader() {
         const fields = [
            {
               key: 'vendor',
               label: '',
               sortable: true,
               thStyle: {fontSize: '.75rem'},
            },
         ];

         this.hasLocation && fields.push(this.manyUSLocations);

         fields.push(
            ...[
               ...this.defaultTotalVendofFields,
               ...this.defaultVendorFields,
               ...this.dimensionFields,
            ]
         );

         return fields;
      },

      period() {
         return this.studyPeriodMap[this.periodId];
      },
      vendorFields() {
         if (!this.adHocData[this.periodId]) return [];
         const rows = Object.values(this.adHocData[this.periodId] ?? {})
            // Employees only exist on the periods they were explicitly added to -- unlike
            // other vendor types, they shouldn't get a blank placeholder row (id === null)
            // seeded on every project-supported period.
            .filter((item) => this.expenseType !== 'employees' || item.id !== null);

         // Filter by the canonical assignee (see canonicalAssigneeId()) instead of the
         // row's own `assigneeId` so the table always agrees with the assignee-status
         // aggregate (mergedStatusesByPeriod/crossPeriodMergedStatuses), which buckets
         // rows the same way.
         const filteredRows = rows.filter(
            (item) => String(this.canonicalAssigneeId(item)) === String(this.assigneeId)
         );

         return filteredRows.map((item) => this.withRowKey(item));
      },

      // Imported employees carry a real .account value (expense_summary.account,
      // written at CSV-import time) -- group them into one table per account,
      // same structure Supplies/Cloud already use for their imported vendors.
      // For contractors/supplies/cloud, ad-hoc rows never have .account, so this
      // is always [] and manualVendorFields below is just the unchanged
      // vendorFields -- zero behavior change for them.
      vendorAccountNames() {
         return [
            ...new Set(this.vendorFields.filter((row) => row.account).map((row) => row.account)),
         ].sort((a, b) => a.localeCompare(b));
      },

      vendorAccountFields() {
         return this.vendorAccountNames.map((account) =>
            this.vendorFields.filter((row) => row.account === account)
         );
      },

      manualVendorFields() {
         return this.vendorFields.filter((row) => !row.account);
      },

      // Unified array-of-arrays for focusCell's bounds-check, mirroring
      // accountFields' shape: indices 0..N-1 are account groups, the last
      // index is the manually-added group.
      vendorSectionTables() {
         return [...this.vendorAccountFields, this.manualVendorFields];
      },

      accountFields() {
         if (!this.accountData[this.periodId]) return [];
         return Object.keys(this.accountData[this.periodId]).map((key) =>
            Object.values(this.accountData[this.periodId][key] ?? {})
               .filter((item) => String(this.canonicalAssigneeId(item)) === String(this.assigneeId))
               .map((item) => this.withRowKey(item))
         );
      },

      hasAnyItems() {
         return Object.values(this.accountFields).some(
            (arr) => Array.isArray(arr) && arr.length > 0
         );
      },

      viewerHasContractorPaymentsPermission() {
         return this.hasContractorPaymentsPermission(this.profile);
      },

      showContractorPaymentsWarning() {
         return (
            String(this.assigneeId) === String(this.profile?.id) &&
            !this.viewerHasContractorPaymentsPermission &&
            this.vendorFields.some((row) => this.isContractorRowLocked(row))
         );
      },
   },

   methods: {
      rowHasProjectSumWarning(row) {
         return hasProjectSumWarningForRow(row);
      },

      rowHasActivitySumWarning(row) {
         return hasActivitySumWarningForRow(row);
      },

      hasContractorPaymentsPermission(user) {
         if (this.expenseType !== 'contractors') return true;
         // Backoffice (manager) view should not be blocked by customer-side permission gates.
         if (this.isRndig) return true;

         return checkContractorPaymentsPermission(user);
      },

      isReadOnlyForAssignee() {
         return this.isRndig
            ? this.isCompleted
            : this.isCompleted || this.assigneeId !== this.profile.id;
      },

      isContractorRowLocked(item) {
         if (this.viewerHasContractorPaymentsPermission) {
            return false;
         }

         // Contractor eligibility is contractor-level (across all period rows), not per-period.
         return !this.isContractorAssignableAcrossPeriods(item);
      },

      isManualVendorInputDisabled(item) {
         return this.isReadOnlyForAssignee() || this.isContractorRowLocked(item);
      },

      // Employee Taxable Wages specifically: a payroll-permission holder (or
      // rndig) can enter it for a manually-added employee even when that
      // employee isn't assigned to them (or isn't assigned to anyone yet) --
      // still locked once the section is completed, same as everyone else.
      isEmployeeWagesInputDisabled(item) {
         if (this.expenseType === 'employees' && this.hasPayrollAccess) {
            return this.isCompleted;
         }
         return this.isManualVendorInputDisabled(item);
      },

      isManualVendorProjectLockedByZeroPercentage(item, projectId) {
         return item?.percentage?.value === 0 && item?.projectsData?.[projectId] === 0;
      },

      // With exactly one project for the period there's nowhere else the split
      // could go -- a positive percentage always auto-fills that project to
      // 100% (see savePercentage) -- so lock it unconditionally, before that
      // fill even happens, rather than only once the value is already 100/0.
      isManualVendorProjectLockedBySingleOption(item, projectId) {
         return this.projects.length === 1 && String(this.projects[0].id) === projectId;
      },

      isManualVendorProjectInputDisabled(item, projectId) {
         return (
            this.isManualVendorInputDisabled(item) ||
            this.isManualVendorProjectLockedByZeroPercentage(item, projectId) ||
            this.isManualVendorProjectLockedBySingleOption(item, projectId)
         );
      },

      isManualVendorActivityLockedByZeroPercentage(item, activityId) {
         return item?.percentage?.value === 0 && item?.activitiesData?.[activityId] === 0;
      },

      // See isManualVendorProjectLockedBySingleOption above -- same reasoning,
      // for the activity dimension.
      isManualVendorActivityLockedBySingleOption(item, activityId) {
         return this.activities.length === 1 && String(this.activities[0].id) === activityId;
      },

      isManualVendorActivityInputDisabled(item, activityId) {
         return (
            this.isManualVendorInputDisabled(item) ||
            this.isManualVendorActivityLockedByZeroPercentage(item, activityId) ||
            this.isManualVendorActivityLockedBySingleOption(item, activityId)
         );
      },

      contractorRowsById(item) {
         if (!item?.contractorId) {
            return [];
         }

         return Object.values(this.adHocData || {}).flatMap((periodRows) =>
            (periodRows || []).filter(({contractorId}) => contractorId === item.contractorId)
         );
      },

      areAllContractorRowsAssignable(contractorRows) {
         return contractorRows.every((row) => row?.assignable === true);
      },

      isContractorAssignableAcrossPeriods(item) {
         const contractorRows = this.contractorRowsById(item);
         if (!contractorRows.length) {
            return item?.assignable === true;
         }

         return this.areAllContractorRowsAssignable(contractorRows);
      },

      hasImplicitContractorPayment(item) {
         if (this.viewerHasContractorPaymentsPermission || !isNil(item?.totalDebit)) {
            return false;
         }

         if (!this.contractorRowsById(item).length) return false;
         return this.isContractorAssignableAcrossPeriods(item);
      },

      manualVendorRowClass(item) {
         if (!item || !this.isContractorRowLocked(item)) {
            return '';
         }

         return 'contractor-payment-locked-row';
      },

      getManualVendorId(item) {
         return item.contractorId ?? item.vendorId ?? item.employeeId;
      },

      // Mirrors resolveCanonicalAssigneeId() (store/utils/expense-data-identity.js),
      // which mergedStatusesByPeriod/crossPeriodMergedStatuses use to bucket rows by
      // assignee. A row's own `assigneeId` is joined server-side from BaseAssignment
      // and can disagree with (or, for stale duplicate BaseAssignment rows, fan out to
      // more than) the canonical assignee on the vendor/employee entity itself -- filter
      // by that canonical value everywhere a table groups rows by assignee, so what's
      // shown always agrees with the assignee-status aggregate.
      canonicalAssigneeId(item) {
         const id = this.getManualVendorId(item);
         const vendor = this.vendorsMap[id] || this.importedVendorsMap[id];
         return vendor ? vendor.assigneeId : item.assigneeId;
      },

      // Imported employees' payroll comes from the CSV upload, same as imported
      // vendors' Net Amount in the account-based table (see #cell(netAmount)/
      // #cell(totalDebit) there) -- read-only label instead of an editable input.
      isImportedEmployeeRow(item) {
         return this.expenseType === 'employees' && item?.uploadFileId != null;
      },

      employeeVendorField(item, fieldKey) {
         const id = this.getManualVendorId(item);
         const vendor = this.vendorsMap[id] || this.importedVendorsMap[id];
         return vendor?.[fieldKey] ?? null;
      },

      hasEmployeeVendorFieldData(fieldKey, rows) {
         return rows.some((item) => {
            const value =
               fieldKey === 'location' ? item.location : this.employeeVendorField(item, fieldKey);
            if (fieldKey === 'officer') return value === true;
            return value !== null && value !== undefined && String(value).trim() !== '';
         });
      },

      // Location/Job Title/Department/Company Officer live on the Employee entity,
      // not the summary row (only location happens to also be a real column on
      // expense_summary) -- looked up the same way the Name column already does,
      // via vendorsMap/importedVendorsMap. Each column is only shown when at least
      // one employee in THIS specific table (one account group, or the manually-
      // added group) actually has data for it -- not globally across every group,
      // so one account's empty Job Title column doesn't force the column to show
      // in every other account's table too.
      employeeDetailFields(rows) {
         if (this.expenseType !== 'employees') return [];

         const fields = [];

         if (this.hasEmployeeVendorFieldData('location', rows)) {
            fields.push(this.manyUSLocations);
         }
         if (this.hasEmployeeVendorFieldData('title', rows)) {
            fields.push({
               key: 'title',
               label: 'Job Title',
               sortable: true,
               formatter: (_val, _key, item) => this.employeeVendorField(item, 'title'),
               sortByFormatted: true,
               thStyle: {fontSize: '.75rem'},
            });
         }
         if (this.hasEmployeeVendorFieldData('department', rows)) {
            fields.push({
               key: 'department',
               label: 'Department',
               sortable: true,
               formatter: (_val, _key, item) => this.employeeVendorField(item, 'department'),
               sortByFormatted: true,
               thStyle: {fontSize: '.75rem'},
            });
         }
         if (this.hasEmployeeVendorFieldData('officer', rows)) {
            fields.push({
               key: 'officer',
               label: 'Company Officer',
               sortable: true,
               formatter: (_val, _key, item) =>
                  this.employeeVendorField(item, 'officer') ? 'Yes' : 'No',
               sortByFormatted: true,
               thStyle: {fontSize: '.75rem'},
            });
         }

         return fields;
      },

      adHocVendorFields(rows) {
         const fields = [
            {
               key: 'vendor',
               label: '',
               sortable: true,
               formatter: (_val, _key, item) => {
                  const id = this.getManualVendorId(item);
                  const vendor = this.vendorsMap[id] || this.importedVendorsMap[id];
                  return vendor ? vendor.fullname ?? vendor.name : '';
               },
               sortByFormatted: true,
               thStyle: {fontSize: '.75rem'},
            },
         ];

         this.hasLocation && fields.push(this.manyUSLocations);

         fields.push(...this.employeeDetailFields(rows));

         fields.push(...[...this.defaultVendorFields, ...this.dimensionFields]);

         return fields;
      },

      getManualVendorPlaceholderValue({manualVendorId, location, path, formatter = (v) => v}) {
         const lastValue = this.previousPeriodIds.reduce((lastValue, id) => {
            const items = this.adHocData[id] || [];
            const item = items.find(
               (item) =>
                  this.getManualVendorId(item) === manualVendorId && item.location === location
            );
            if (!item) return lastValue;

            const value = path.reduce((val, key) => val?.[key] ?? null, item);
            return value !== null ? value : lastValue;
         }, null);

         return lastValue !== null ? String(formatter(lastValue)) : null;
      },

      getImportedVendorPlaceholderValue({vendor, location, account, path, formatter = (v) => v}) {
         const lastValue = this.previousPeriodIds.reduce((lastValue, id) => {
            if (!this.accountData[id]) return lastValue;

            const items = this.accountData[id][account] || [];
            const item = items.find((item) => item.vendor === vendor && item.location === location);
            if (!item) return lastValue;

            const value = path.reduce((val, key) => val?.[key] ?? null, item);
            return value !== null ? value : lastValue;
         }, null);

         return lastValue !== null ? String(formatter(lastValue)) : null;
      },

      accounts(periodId) {
         return periodId in this.accountData ? Object.keys(this.accountData[periodId]) : [];
      },

      resetSelections(currentPeriodId) {
         this.accounts(currentPeriodId).forEach((_, index) => {
            this.unselectAllCompanies(false, index);
         });
         this.unselectAllVendors(false);
      },

      unselectAllCompanies(value, accountIndex) {
         if (this.accountFields.length > 0) {
            this.accountFields[accountIndex].forEach((a) => {
               if (!this.companySelection[accountIndex])
                  Vue.set(this.companySelection, accountIndex, {});
               Vue.set(this.companySelection[accountIndex], a.id, value);
            });
         }
      },

      // rows defaults to the full vendorFields (used by resetSelections, which
      // means "reset everyone" on a period change) -- template call sites pass
      // the specific group's rows so each table's controls only affect its own
      // employees, not every group at once.
      unselectAllVendors(value, rows = this.vendorFields) {
         if (rows.length > 0) {
            rows.forEach((a) => {
               if (this.isContractorRowLocked(a)) {
                  Vue.set(this.vendorSelection, this.getManualVendorId(a), false);
                  return;
               }

               Vue.set(this.vendorSelection, this.getManualVendorId(a), value);
            });
         }
      },

      anyCompanySelected(accountIndex) {
         return this.accountFields[accountIndex].some(({id}) =>
            this.companySelection[accountIndex] ? this.companySelection[accountIndex][id] : false
         );
      },

      anyVendorSelected(rows = this.vendorFields) {
         return rows.some((item) => this.vendorSelection[this.getManualVendorId(item)]);
      },

      // "select all" checked-state for one group's table, computed on demand
      // instead of a single flat flag -- vendorSelection itself stays a flat,
      // row-identity-keyed dictionary (an employee id is already unique across
      // every group, so no per-group nesting is needed there).
      isVendorGroupFullySelected(rows) {
         const visibleUnlockedIds = rows
            .filter((item) => !this.isContractorRowLocked(item))
            .map((item) => String(this.getManualVendorId(item)));
         return (
            visibleUnlockedIds.length > 0 &&
            visibleUnlockedIds.every((id) => this.vendorSelection[id])
         );
      },

      syncVendorSelection() {
         const visibleUnlockedVendorIds = new Set(
            this.vendorFields
               .filter((item) => !this.isContractorRowLocked(item))
               .map((item) => String(this.getManualVendorId(item)))
         );

         Object.keys(this.vendorSelection).forEach((vendorId) => {
            if (!visibleUnlockedVendorIds.has(String(vendorId))) {
               Vue.delete(this.vendorSelection, vendorId);
            }
         });
      },

      selectedVendorIds(rows = this.vendorFields) {
         if (this.anyVendorSelected(rows)) {
            return Object.entries(this.vendorSelection)
               .filter((entry) => {
                  return entry[1];
               })
               .map((entry) => entry[0]);
         } else {
            return rows
               .filter((item) => !this.isContractorRowLocked(item))
               .map((v) => this.getManualVendorId(v));
         }
      },

      selectedCompanyIds(accountIndex) {
         if (this.anyCompanySelected(accountIndex)) {
            return Object.entries(this.companySelection[accountIndex])
               .filter((entry) => {
                  return entry[1];
               })
               .map((entry) => entry[0]);
         } else {
            return (this.accountFields[accountIndex] || []).map((a) => a.id);
         }
      },

      async setCompaniesToZero(accountIndex) {
         const selectedIds = this.selectedCompanyIds(accountIndex);
         const summaries = this.accountFields[accountIndex].filter(({id}) =>
            selectedIds.includes(String(id))
         );

         if (summaries.length) {
            await this.$store.dispatch(`${this.expenseType}/setZero`, {
               companyId: this.$companyId,
               summaries,
            });
         }

         this.selectAllCompanies = [];
         this.unselectAllCompanies(false, accountIndex);
      },

      async setVendorsToZero(rows = this.vendorFields) {
         const selectedIds = this.selectedVendorIds(rows);
         const summaries = rows.filter((item) =>
            selectedIds.includes(String(this.getManualVendorId(item)))
         );

         if (summaries.length) {
            await this.$store.dispatch(`${this.expenseType}/setZero`, {
               companyId: this.$companyId,
               summaries,
            });
         }

         this.unselectAllVendors(false, rows);
      },

      isNegativeImportedNetAmount(item) {
         return Number(item?.netAmount?.value) <= 0;
      },

      isAccountPercentageInputDisabled(item) {
         return this.isReadOnlyForAssignee() || this.isNegativeImportedNetAmount(item);
      },

      isAccountProjectLockedByZeroPercentage(item, projectId) {
         return item?.percentage?.value === 0 && item?.projectsData?.[projectId] === 0;
      },

      // With exactly one project for the period there's nowhere else the split
      // could go, so lock it unconditionally -- mirrors
      // isManualVendorProjectLockedBySingleOption above. Imported rows need this
      // too, or the field is left editable until a positive percentage triggers
      // the auto-fill, and a stray blur on it can silently overwrite the
      // auto-filled value back to empty (see isAccountActivityLockedBySingleOption
      // below for the activity case this was actually caught from).
      isAccountProjectLockedBySingleOption(item, projectId) {
         return this.projects.length === 1 && String(this.projects[0].id) === projectId;
      },

      isAccountProjectInputDisabled(item, projectId) {
         return (
            this.isAccountPercentageInputDisabled(item) ||
            this.isAccountProjectLockedByZeroPercentage(item, projectId) ||
            this.isAccountProjectLockedBySingleOption(item, projectId)
         );
      },

      isAccountActivityLockedByZeroPercentage(item, activityId) {
         return item?.percentage?.value === 0 && item?.activitiesData?.[activityId] === 0;
      },

      // See isAccountProjectLockedBySingleOption above -- same reasoning, for the
      // activity dimension. Without this, an imported row's auto-filled
      // single-activity value stays editable, and ActivityPercentageInput's own
      // independent saveActivityPercentage save path (keyed separately from
      // savePercentage's own project/activity follow-up writes) can fire on a
      // stray blur and silently overwrite the just-written 100% back to empty --
      // reverting on refresh since the empty value is what actually persists.
      isAccountActivityLockedBySingleOption(item, activityId) {
         return this.activities.length === 1 && String(this.activities[0].id) === activityId;
      },

      isAccountActivityInputDisabled(item, activityId) {
         return (
            this.isAccountPercentageInputDisabled(item) ||
            this.isAccountActivityLockedByZeroPercentage(item, activityId) ||
            this.isAccountActivityLockedBySingleOption(item, activityId)
         );
      },

      importedRowNeedsNegativeNetAmountZero(item) {
         if (!this.isNegativeImportedNetAmount(item)) {
            return false;
         }

         if (item?.percentage?.value !== 0) {
            return true;
         }

         return Object.values(item?.projectsData || {}).some((value) => value !== 0);
      },

      async normalizeNegativeImportedNetAmounts() {
         if (this.normalizingNegativeAccountRows || !this.loaded) {
            return;
         }

         const rowsToNormalize = this.accountFields
            .flat()
            .filter((item) => this.importedRowNeedsNegativeNetAmountZero(item));

         if (!rowsToNormalize.length) {
            return;
         }

         this.normalizingNegativeAccountRows = true;

         try {
            await this.$store.dispatch(`${this.expenseType}/setZero`, {
               companyId: this.$companyId,
               summaries: rowsToNormalize,
            });
         } finally {
            this.normalizingNegativeAccountRows = false;
         }
      },

      vendorStatus(item) {
         const hasVendorId = !!this.getManualVendorId(item);
         return dataStatus({
            projectsData: item.projectsData,
            activitiesData: item.activitiesData,
            percentage: item.percentage?.value,
            totalDebit: this.statusTotalDebitForRow(item),
            hasVendorId,
            hasActivitiesDimension: this.hasActivitiesDimension,
            validProjectIds: this.projects.map(({id}) => String(id)),
            validActivityIds: this.activities.map(({id}) => String(id)),
            // Imported employees always arrive with wages already populated from
            // the CSV -- only manually-added employees actually need that field
            // checked as a completion gate. Non-employee rows keep the original
            // hasVendorId-driven behavior untouched (isImportedEmployeeRow is
            // always false for them).
            requiresNetAmount:
               hasVendorId &&
               !this.isImportedEmployeeRow(item) &&
               !(this.expenseType === 'employees' && !this.hasPayrollAccess),
         });
      },

      getStatusSortKey(item) {
         return this.computeRowKey(item);
      },

      statusSortValue(item) {
         const key = this.getStatusSortKey(item);
         const status =
            key in this.statusSortSnapshots
               ? this.statusSortSnapshots[key]
               : this.vendorStatus(item);

         switch (status) {
            case this.$constants().StatusType.INCOMPLETE:
               return 0;
            case this.$constants().StatusType.IN_PROGRESS:
               return 10;
            case this.$constants().StatusType.COMPLETE:
               return 20;
            case this.$constants().StatusType.NA:
               return -1;
            default:
               return -1;
         }
      },

      onTableSortChanged({sortBy}, items) {
         if (sortBy !== 'status') {
            this.statusSortSnapshots = {};
            return;
         }

         const snapshots = {};
         (items || []).forEach((item) => {
            const key = this.getStatusSortKey(item);
            snapshots[key] = this.vendorStatus(item);
         });
         this.statusSortSnapshots = snapshots;
      },

      projectColKey(projectId) {
         return `cell(project_${projectId})`;
      },

      projectDataPath(projectId) {
         return ['projectsData', String(projectId)];
      },

      activityColKey(activityId) {
         return `cell(activity_${activityId})`;
      },

      activityDataPath(activityId) {
         return ['activitiesData', String(activityId)];
      },

      onStepClick() {
         this.step = this.step === 0 ? 1 : 0;
      },

      computeRowKey(item) {
         const manualVendorId = item.contractorId ?? item.vendorId ?? item.employeeId;
         const scope = !isNil(manualVendorId) ? 'manual' : 'account';
         return `${scope}:${item.id ?? ''}:${manualVendorId ?? ''}:${item.account ?? ''}:${
            item.vendor ?? ''
         }:${item.location ?? ''}`;
      },

      withRowKey(item) {
         const row = {...item};
         Object.defineProperty(row, '_rowKey', {
            value: this.computeRowKey(item),
            enumerable: false,
            configurable: true,
         });
         return row;
      },

      formatMoney(cents) {
         if (cents === null || cents === undefined) return '';
         const formatter = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
         });
         return formatter.format(cents / 100);
      },

      getImportedStatusKey(item) {
         if (!isNil(item?.id)) {
            return `account:${item.id}`;
         }

         return `account-fallback:${item?.account ?? ''}:${item?.vendor ?? ''}:${
            item?.location ?? ''
         }`;
      },

      getManualStatusKey(item) {
         return `manual:${this.getManualVendorId(item) ?? ''}:${item?.location ?? ''}`;
      },

      statusTotalDebitForRow(item) {
         if (this.expenseType === 'contractors' && this.hasImplicitContractorPayment(item)) {
            return 0;
         }

         return item.totalDebit;
      },

      async loadData() {
         this.loaded = false;
         this.statusSortSnapshots = {};
         await this.$store.dispatch('projects/loadProjects', {
            companyId: this.$companyId,
         });
         if (this.hasActivitiesDimension) {
            await this.$store.dispatch('activities/loadActivities', {
               companyId: this.$companyId,
            });
         }
         this.resetSelections(this.periodId);
         this.loaded = true;
         await this.normalizeNegativeImportedNetAmounts();
      },
   },

   watch: {
      async periodId(newPeriod, oldPeriod) {
         if (+newPeriod !== +oldPeriod) {
            this.statusSortSnapshots = {};
            this.resetSelections(newPeriod);
         }
      },
      assigneeId() {
         this.statusSortSnapshots = {};
         this.resetSelections(this.periodId);
      },
      vendorFields: {
         handler() {
            this.syncVendorSelection();
         },
         deep: true,
      },
      accountFields: {
         handler() {
            this.resetSelections(this.periodId);
            this.normalizeNegativeImportedNetAmounts();
         },
         deep: true,
      },
   },

   async created() {
      await this.loadData(this.periodId);
   },
};
</script>

<style lang="scss" scoped>
::v-deep {
   .b-table-sticky-header {
      scroll-padding-top: 95px;
   }

   .b-table {
      font-size: 15px;
   }

   .table-supplies-vendors {
      font-size: 0.75rem;
   }
   .invalid-feedback {
      white-space: nowrap;
   }
   @supports (position: sticky) {
      .b-table-sticky-header > .table.b-table > thead > tr > th {
         position: sticky;
         top: 35px;
         z-index: 2;
      }
      tr {
         td:first-child,
         th:first-child {
            position: sticky;
            left: 0;
            background-color: #fff;
            z-index: 4 !important;
         }
         th:first-child {
            z-index: 5 !important;
         }
      }
   }
}

.account-section {
   box-shadow: none !important;
   padding: 0px !important;

   @include lg {
      margin-top: 1.5rem;
   }

   .account-data {
      display: flex;
      flex-direction: column-reverse;
      align-items: stretch;
      justify-content: space-between;

      @include lg {
         // padding-left: 3rem;
         flex-direction: row;
      }
   }
}
.scroll-x {
   overflow-x: auto;
   width: 100%;
   box-sizing: border-box;
}

.table-info-row {
   background-color: #e6f1fb4d;
   border: 1px solid #0778cd33;
   border-radius: 1rem;
   width: 100%;
   margin: 0;
   height: 1.625rem;
   text-align: left;
   padding-left: 0.75rem;
   padding-top: 0.1875rem;
   font-size: 10px;
   white-space: nowrap;
}

.contractor-lock-note {
   display: inline-block;
   margin-left: 0.5rem;
   font-size: 12px;
   color: #8a6d3b;
}

::v-deep .contractor-payment-locked-row td {
   opacity: 0.6;
}

.permission-warning-alert {
   font-size: 12px;
   line-height: 1.4;
}

.table-supplies-vendors td:has(.time-percentage-input) {
   vertical-align: top;
}

.top-heading-row {
   th {
      z-index: 6 !important;
      top: 0 !important;
   }
}

th {
   top: 2.1rem !important;
}

.year-selection {
   margin-bottom: 2rem;

   .year-selection-row {
      display: flex;
      justify-content: space-between;
      align-items: center;

      .current-period {
         color: inherit;
         font-size: 1.25rem;
      }

      .periods-button-wrapper {
         display: flex;
         justify-content: space-between;
         align-items: center;
         gap: 0.625rem;
      }

      .year-nav-btn button {
         border: 0;
         background-color: transparent;
         color: $secondary;
         transition: all 0.2s ease;

         &:hover {
            color: $primary;
            text-decoration: underline;
         }

         svg {
            margin-bottom: 0.6em;
         }
      }
   }
}
.card-description {
   font-size: 12px;
}
.assignments-card {
   border-radius: 20px;
   box-shadow: 0 4px 20px 0 #005d8b26 !important;
   margin-right: 50px;
   margin-left: 50px;
   width: 100%;
}

.fluid-transition {
   width: 82%;
}

.dimension-tab-switch {
   display: flex;
   flex-direction: row;
   justify-content: flex-end;
   gap: 0.5rem;
   margin: 3px;
}

.step-button {
   margin-right: 10px;
}

.action-button {
   font-size: 0.75rem;
}

.dimension-slide-forward-enter-active,
.dimension-slide-forward-leave-active,
.dimension-slide-back-enter-active,
.dimension-slide-back-leave-active {
   transition: transform 0.35s ease, opacity 0.35s ease;
}

.dimension-slide-forward-enter {
   transform: translateX(30px);
   opacity: 0;
}
.dimension-slide-forward-leave-to {
   transform: translateX(-30px);
   opacity: 0;
}

.dimension-slide-back-enter {
   transform: translateX(-30px);
   opacity: 0;
}
.dimension-slide-back-leave-to {
   transform: translateX(30px);
   opacity: 0;
}

.status-class {
   font-size: 16px;
}

.account-group-title {
   position: relative;
   margin: 0.75rem 0 0.625rem;
   font-size: 0.875rem;
   font-weight: 400;
   color: #8a8a8a;
   line-height: 1.2;
}

.stay-visible {
   position: relative;
   z-index: 1000;
}

.supplies-selection {
   margin-bottom: 2rem;

   .supplies-selection-row {
      width: 100%;
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;

      h1 {
         color: inherit;
         margin: 0;
      }

      .supplies-nav-btn button {
         border: 0;
         background-color: transparent;
         color: $secondary;
         transition: all 0.2s ease;

         &:hover {
            color: $primary;
            text-decoration: underline;
         }

         svg {
            margin-bottom: 0.6em;
         }
      }
   }
}
</style>
